import React, { HTMLAttributes, SyntheticEvent, useState } from "react";
import { Button, Row, Space, Table, TableProps, Tooltip } from "antd";
import { EntitlementModel } from "types/model/entitlement";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "app/store";
import { useParams } from "react-router";
import { unwrapResult } from "@reduxjs/toolkit";
import useAppAuth0 from "hooks/useAppAuth0";
import { message } from "components";
import { ResizeCallbackData } from "react-resizable";
import { ColumnsType, ColumnType } from "antd/es/table";
import { CustomerTab } from "../CustomerList/CustomerList";
import { useColumnsB2C, useColumnsB2B } from "./columnConfig";
import { fetchEntitlementList, restoreEntitlement } from "../customerSlice";
import styles from "./CustomerEntitlements.module.scss";
import AddEntitlement from "../AddEntitlement";
import ResizableTitle from "../../../components/ResizableTitle";

interface Props {
  tab: CustomerTab;
  auth0Id?: string;
}

const handleWidthBoundMap = {
  B2B: (width: number) => {
    if (width < 115) {
      return 115;
    } else if (width > 535) {
      return 535;
    }
    return width;
  },
  B2C: (width: number) => {
    if (width < 115) {
      return 115;
    } else if (width > 361) {
      return 361;
    }
    return width;
  },
};

function CustomerEntitlements({ auth0Id, tab }: Props) {
  const dispatch = useAppDispatch();
  const { user } = useAppAuth0();
  const { customerId } = useParams<{
    customerId: string;
  }>();
  const columnsB2C = useColumnsB2C();
  const columnsB2B = useColumnsB2B();
  const columns = tab === "B2C" ? columnsB2C : columnsB2B;
  const [tableColumns, setTableColumns] = useState<ColumnsType<EntitlementModel>>(columns);
  const handleResize = (index: number) => (_: SyntheticEvent, { size }: ResizeCallbackData) => {
    const nextColumns = [...columns];
    nextColumns[index] = {
      ...nextColumns[index],
      width: handleWidthBoundMap[tab](size.width),
    };
    setTableColumns(nextColumns);
  };
  const resizableColumns = tableColumns.map((col, index) => ({
    ...col,
    onHeaderCell: (column: ColumnType<EntitlementModel>) =>
      ({
        width: column.width,
        onResize: handleResize(index),
      } as HTMLAttributes<HTMLElement>),
  }));

  const { customersEntitlements } = useSelector((state: RootState) => state.customer);
  const { REACT_APP_B2B_WEB_URL } = process.env;

  const handleRestore = () => {
    customerId &&
      dispatch(restoreEntitlement(Number(customerId)))
        .then(unwrapResult)
        .then(() =>
          dispatch(fetchEntitlementList(Number(customerId)))
            .then(unwrapResult)
            .catch(message.error)
        )
        .catch(message.error);
  };

  const handleOnRowClick: TableProps<EntitlementModel>["onRow"] = (row) => ({
    onClick: () => {
      if (
        tab === "B2B" &&
        row.status !== "REVOKED" &&
        row.adminGroupId &&
        row.voucherGroupId &&
        row.voucherCode &&
        row.voucherType
      ) {
        if (row.source === "B2B_SEAT_ASSIGNMENT") {
          window.open(
            `${REACT_APP_B2B_WEB_URL}/${encodeURIComponent(row.adminGroupId)}/assignment/${encodeURIComponent(
              row.voucherGroupId
            )}?assigneeEmail=${encodeURIComponent(row.customerEmail)}`
          );
        } else if (row.source === "B2B_REDEMPTION") {
          if (row.voucherType === "SINGLE") {
            window.open(
              `${REACT_APP_B2B_WEB_URL}/${encodeURIComponent(row.adminGroupId)}/assignment/${encodeURIComponent(
                row.voucherGroupId
              )}?voucherCode=${encodeURIComponent(row.voucherCode)}`
            );
          } else if (row.voucherType === "MASTER") {
            window.open(
              `${REACT_APP_B2B_WEB_URL}/${encodeURIComponent(row.adminGroupId)}/assignment/${encodeURIComponent(
                row.voucherGroupId
              )}?redeemerEmail=${encodeURIComponent(row.customerEmail)}`
            );
          }
        }
      }
    },
  });

  return (
    <Space size={24} direction={"vertical"} className={styles.entitlementSection}>
      <Row justify={"space-between"}>
        <header className={styles.subtitle}>Entitlement</header>
        {tab === "B2C" && (
          <div>
            {user.canRestoreEntitlements && (
              <Tooltip placement="topLeft" title="Only for eCom and Apple store entitlement">
                <Button type="default" shape="round" style={{ marginRight: "8px" }} onClick={handleRestore}>
                  Restore
                </Button>
              </Tooltip>
            )}
            {user.canCreateB2CCustomerEntitlement && <AddEntitlement auth0Id={auth0Id} />}
          </div>
        )}
      </Row>
      <Table
        scroll={{ x: "100%" }}
        className={styles.table}
        components={{ header: { cell: ResizableTitle } }}
        columns={resizableColumns}
        dataSource={customersEntitlements}
        rowKey={(row) => row.entitlementId}
        rowClassName={(row) =>
          tab === "B2C"
            ? styles.arrow
            : row.status === "REVOKED" ||
              !row.adminGroupId ||
              !row.voucherGroupId ||
              !row.voucherCode ||
              !row.voucherType
            ? styles.disable
            : row.source === "B2B_REDEMPTION" || row.source === "B2B_SEAT_ASSIGNMENT"
            ? styles.hand
            : styles.arrow
        }
        onRow={handleOnRowClick}
        pagination={false}
      />
    </Space>
  );
}

export default CustomerEntitlements;
